export const WUBRG = Object.freeze(["W", "U", "B", "R", "G"]);
// WUBRG-ordered color combinations
// https://boardgames.stackexchange.com/questions/54638
// https://www.reddit.com/r/magicTCG/comments/5ybsgw/guide_mtg_colour_pie_for_dummies_allies_enemies/
const AllyColors = Object.freeze(["WU", "UB", "BR", "RG", "GW"]);
const EnemyColors = Object.freeze(["WB", "UR", "BG", "RW", "GU"]);
// Shards: primary color + two allies, sorted by primary/middle color
const ShardColors = Object.freeze(["GWU", "WUB", "UBR", "BRG", "RGW"]);
// Wedges: primary color + two enemies, sorted by primary/middle color
const WedgeColors = Object.freeze(["RWB", "GUR", "WBG", "URW", "BGU"]);
// Four-color combinations
const FourColors = Object.freeze(["WUBR", "UBRG", "BRGW", "RGWU", "GWUB"]);

/**
 * An ordered list of all explicit WUBRG-ordered color combinations.
 */
const OrderedColorCombinations = Object.freeze(WUBRG
  .concat(AllyColors)
  .concat(EnemyColors)
  .concat(ShardColors)
  .concat(WedgeColors)
  .concat(FourColors)
  .concat("WUBRG"));

export const Colorless = "C";
export const Multicolor = "multi";

export const Land = "land";
export const BasicLand = "basic land"

export const Special = "special";
export const Unknown = "unknown";

/**
 * @param {String} colors one or more card colors
 * @returns the individual color characters, sorted alphabetically
 */
function sortedColors(colors) {
  return colors.split('').sort().join('');
}

/**
 * A map from colors with the individual color codes sorted alphabetically
 * to the corresponding WUBRG-ordered color combination.
 */
export const ColorCombinationMap = Object.freeze(OrderedColorCombinations.reduce(
  (map, colors) => ({ ...map, [sortedColors(colors)]: colors }),
  /* initialValue = */ {}));

/**
 * An ordered list of *all* color *combinations* and card groupings, with each 
 * individual color character sorted alphabetically.
 */
export const OrderedColorCombinationsAndGroups = 
  Object.freeze(OrderedColorCombinations.map(sortedColors).concat(
    Colorless,
    // a special column (e.g., order Lessons separately)
    Special, 
    Land,
    BasicLand
  ));

/**
 * Ordered individual colors and card gropuings that include a generic {@link Multicolor} 
 * category for all multicolor cards, unlike {@link OrderedColorCombinationsAndGroups} 
 * which separates each individual color combinations/factions.
 */
export const OrderedColorsAndGroups = Object.freeze(WUBRG
  .concat(
    Multicolor, 
    Colorless, 
    // a special column (e.g., order Lessons separately)
    Special, 
    Land, 
    BasicLand
  ));

export const OrderedRarities = Object.freeze(["mythic", "rare", "uncommon", "common", BasicLand]);

export const OrderedTierRatings = Object.freeze([
  { tier: "A+", rating: "10",  color: "lime"}, // 10: Bomb
  { tier: "A",  rating: "9.5", color: "greenyellow"},
  { tier: "A-", rating: "9",   color: "greenyellow"},
  { tier: "B+", rating: "8.5", color: "greenyellow"},
  { tier: "B",  rating: "8",   color: "greenyellow"},
  { tier: "",   rating: "7.5", color: "yellow"},
  { tier: "B-", rating: "7",   color: "yellow"},
  { tier: "",   rating: "6.5", color: "gold"},
  { tier: "C+", rating: "6",   color: "gold"}, // 6-9: Almost always play
  { tier: "",   rating: "5.5", color: "orange"},
  { tier: "C",  rating: "5",   color: "orange"},
  { tier: "C-", rating: "",    color: "orange"},
  { tier: "",   rating: "4.5", color: "#f88"},
  { tier: "D+", rating: "4",   color: "#f88"},
  { tier: "",   rating: "3.5", color: "#f88"},
  { tier: "D",  rating: "3",   color: "#f88"}, // 3-5: Filler
  { tier: "",   rating: "2.5", color: "#f57"},
  { tier: "D-", rating: "2",   color: "#f57"},
  { tier: "",   rating: "1.5", color: "#f57"},
  { tier: "F",  rating: "1",   color: "#f57"}, // 1-2: Bad/Sideboard
  { tier: "SB", rating: "SB",  color: "#ccc"},
]);

/**
 * OrderedTierRatings mapped to just the tier
 */
export const OrderedTiers = Object.freeze(OrderedTierRatings.map(t => t.tier).filter(t => t));

/**
 * OrderedTierRatings mapped to just the rating, filtering out empty ratings
 */
export const OrderedRatings = Object.freeze(OrderedTierRatings.map(t => t.rating).filter(r => r));

/**
 * Color symbols for mana sources
 */
export const ManaSourceColors = Object.freeze(WUBRG.concat("C"));

/**
 * Mapping from remastered set codes to the original set code(s)
 */
export const RemasteredSetCodes = Object.freeze({
  // Kaladesh Remastered: Kaladesh and Aether Revolt
  klr: [ "kld", "aer" ],
  // Amonkhet Remastered: Amonkhet and Hour of Devastation
  akr: [ "akh", "hou" ],
  // Shadows over Innistrad Remastered: Shadows over Innistrad and Eldritch Moon
  sir: [ "soi", "emn" ]
});

/**
 * Merge two set-mapping objects (e.g. setCode -> related setCodes).
 *
 * Ensures:
 *   - All set codes are preserved
 *   - Related codes are deduplicated
 *   - Original inputs remain immutable
 * 
 * @param {Object<string, string[]>} base
 * @param {Object<string, string[]>} other
 * @returns {Object<string, string[]>}
 */
const mergeSetCodeMappings = (a, b) =>
  Object.entries(b).reduce((acc, [key, values]) => {
    acc[key] = key in acc
      ? [...new Set([...acc[key], ...values])]
      : values;
    return acc;
  }, { ...a });

/**
 * Mapping from standard set codes to bonus sheet set codes with a dedicated slot in booster packs.
 * 
 * These sets will be loaded as part of Set and Tier List card pools.
 */
export const DedicatedBonusSheetSetCodes = Object.freeze({
  // STX -> STA (Strixhaven: Mystical Archive)
  stx: [ "sta" ],
  // BRO -> BRR (The Brothers' War Retro Artifacts)
  bro: [ "brr" ],
  // SIR (Shadows over Innistrad Remastered) -> SIS (Shadows of the Past)
  sir: [ "sis" ],
  // MOM -> MUL (March of the Machine Multiverse Legends)
  mom: [ "mul" ],
  // WOE -> WOT (Wilds of Eldraine: Enchanting Tales)
  woe: [ "wot" ], 
  // OTJ -> OTP (Breaking News) and BIG (The Big Score)
  otj: [ "otp" ],
  // SOS -> SOA (Secrets of Strixhaven: Mystical Archive)
  sos: [ "soa"],
});

/**
 * Mapping from standard set codes to all NON-dedicated bonus sheet set codes.
 */
export const BonusSheetSetCodes = Object.freeze(
  // The mappings defined here are more like Special Guests, which will not be
  // loaded for Set and Tier List card pools; merge them with the mappings for
  // dedicated bonus sheet set codes.
  mergeSetCodeMappings({
    // OTJ -> BIG (The Big Score)
    otj: [ "big" ],
    // MOM -> MAT (March of the Machine: The Aftermath)
    // Arena draft format with a dedicated slot for MAT cards
    mom: [ "mat" ],
    // FIN -> FCA (Final Fantasy: Through the Ages)
    // No Dedicated Slot; ugly Source Material cards
    fin: [ "fca" ], 
    // EOE -> EOS (Edge of Eternities: Stellar Sights)
    // No Dedicated Slot; 1 out of 8 Boosters
    eoe: [ "eos" ],
    // SPM -> MAR (Marvel Universe)
    //TODO ? 'Marvel Universe' may cover multiple Marvel-themed sets in the future
    // No Dedicated Slot; ugly Source Material cards
    spm: [ "mar" ],
    // OM1 -> OMB (Through the Omenpaths Bonus Sheet)
    // Omenpath version of MAR cards
    om1: [ "omb" ],
    // TLA -> TLE (Avatar: The Last Airbender Eternal)
    // No Dedicated Slot; ugly Source Material cards
    tla: [ "tle" ],
    // TMT -> PZA ("Pizza"; Teenage Mutant Ninja Turtles Source Material)
    // No Dedicated Slot; ugly Source Material cards
    tmt: [ "pza" ],
  },
  DedicatedBonusSheetSetCodes)
);

/**
 * A list of set codes that were drafted as Play Boosters and included 'Special Guests' for drafting
 * 
 * https://mtg.wiki/page/Special_Guests
 */
export const PlayBoosterSetCodes = Object.freeze([
  //'lci', // Lost Caverns of Ixalan (had Special Guests but not drafted as Play Boosters)
  'mkm', // Murders at Karlov Manor
  'otj', // Outlaws of Thunder Junction
  'mh3', // Modern Horizons 3
  'blb', // Bloomburrow
  'dsk', // Duskmourn
  'fdn', // Foundations
  'dft', // Aetherdrift
  'tdm', // Tarkir: Dragonstorm
  'eoe', // Edge of Eternities
  'ecl', // Llorwyn Eclipsed
  'sos', // Streets of Strixhaven
  'fra', // Reality Fracture
]);

/**
 * The number of digits to use for displaying the 17Lands 
 * Games In /hand Win Rate.
 * 
 * For example, 3 digits of 0.54734 as a percentile means 54.7%
 */
export const GIH_WR_DIGITS = 3;

export const GIH_WR_PROPERTY    = "17lands_ever_drawn_win_rate";
export const GIH_COUNT_PROPERTY = "17lands_ever_drawn_game_count";
export const GIH_SET_PROPERTY   = "17lands_card_ratings_set";

export const RATING_SCORES_PROPERTY = "rating_scores";
export const RATING_TIER_PROPERTY   = "rating_tier";
export const RATING_SET_PROPERTY    = "rating_set";

/**
 * Basic land types and their color symbols (Excluding Wastes/Colorless)
 */
export const BasicLandColors = Object.freeze({
  "plains" : "W",
  "island" : "U",
  "swamp" : "B",
  "mountain" : "R",
  "forest" : "G"
});

/**
 * The standard basic land types (Excluding Wastes)
 */
 export const BasicLandTypes = Object.freeze(Object.keys(BasicLandColors));

/**
 * Fetch lands that fetch basic land *types* 
 * (not restricted to basic lands);
 * 
 * See https://mtg.wiki/page/Fetch_land
 * Scryfall: https://scryfall.com/search?q=otag%3Afetch-land
 */
 export const BasicTypeFetchLands = Object.freeze({
  // Miscellaneous
  //  ignore "Flagstones of Trokair" -- Plains -- must be sacrificed
  //  ignore "Maze's End" -- fetches Gates
  // Mirage (Allied Fetch Lands)
  "flood plain":     [ "plains",   "island" ],
  "bad river":       [ "island",   "swamp" ],
  "rocky tar pit":   [ "swamp",    "mountain" ],
  "mountain valley": [ "mountain", "forest" ],
  "grasslands":      [ "forest",   "plains" ],
  // Onslaught (Allied Pain Fetch Lands)
  "flooded strand":    [ "plains",   "island" ],
  "polluted delta":    [ "island",   "swamp" ],
  "bloodstained mire": [ "swamp",    "mountain" ],
  "wooded foothills":  [ "mountain", "forest" ],
  "windswept heath":   [ "forest",   "plains" ],
  // Zendikar (Enemy Pain Fetch Lands)
  "marsh flats":       [ "plains",   "swamp" ],
  "scalding tarn":     [ "island",   "mountain" ],
  "verdant catacombs": [ "swamp",    "forest" ],
  "arid mesa":         [ "mountain", "plains" ],
  "misty rainforest":  [ "forest",   "island" ],
  // "Krosan Verge" -- two lands: Forest and Plains
  "krosan verge":      [ "forest", "plains" ],
  // "Urza's Cave" -- any land type (even non-basic types)
  // Urza's Cave can fetch colorless lands, but it also produces colorless mana
  // so there is no need to support "wastes" as a fetchable land type here
  "urza's cave":       [ "plains", "island", "swamp", "mountain", "forest" ],
});

/**
 * Fetch lands that fetch basic lands;
 * 
 * See https://mtg.wiki/page/Fetch_land
 * Scryfall: https://scryfall.com/search?q=otag%3Afetch-land
 */
export const BasicFetchLands = Object.freeze({
  // Miscellaneous
  //  ignore "Blighted Woodland" -- green to activate
  //  ignore "Demolition Field" -- destroy opponent's nonbasic to activate
  //  ignore "Field of Ruin" -- destroy opponent's nonbasic to activate
  // Any Basic Land
  "fabled passage":       [ "plains", "island", "swamp", "mountain", "forest" ],
  "prismatic vista":      [ "plains", "island", "swamp", "mountain", "forest" ],
  // "Myriad Landscape" -- two basic lands of the same type
  "myriad landscape":     [ "plains", "island", "swamp", "mountain", "forest" ],
  "terminal moraine":     [ "plains", "island", "swamp", "mountain", "forest" ],
  "warped landscape":     [ "plains", "island", "swamp", "mountain", "forest" ],
  "terramorphic expanse": [ "plains", "island", "swamp", "mountain", "forest" ],
  "evolving wilds":       [ "plains", "island", "swamp", "mountain", "forest" ],
  "thawing glaciers":     [ "plains", "island", "swamp", "mountain", "forest" ],
  "shire terrace":        [ "plains", "island", "swamp", "mountain", "forest" ],
  "promising vein":       [ "plains", "island", "swamp", "mountain", "forest" ],
  "vibrant cityscape":    [ "plains", "island", "swamp", "mountain", "forest" ],
  "escape tunnel":        [ "plains", "island", "swamp", "mountain", "forest" ],
  // Shards of Alara (Panoramas)
  "bant panorama":   [ "forest",   "plains",   "island" ],
  "esper panorama":  [ "plains",   "island",   "swamp" ],
  "grixis panorama": [ "island",   "swamp",    "mountain" ],
  "jund panorama":   [ "swamp",    "mountain", "forest" ],
  "naya panorama":   [ "mountain", "forest",   "plains" ],
  // Streets of New Capenna (Family Fetch Lands)
  "brokers hideout":     [ "forest",   "plains",   "island" ],
  "obscura storefront":  [ "plains",   "island",   "swamp" ],
  "maestros theater":    [ "island",   "swamp",    "mountain" ],
  "riveteers overlook":  [ "swamp",    "mountain", "forest" ],
  "cabaretti courtyard": [ "mountain", "forest",   "plains" ],
  // Modern Horizons 3 (Landscapes)
  "bountiful landscape":    [ "forest",   "island",   "mountain" ],
  "contaminated landscape": [ "plains",   "island",   "swamp" ],
  "deceptive landscape":    [ "plains",   "swamp",    "forest" ],
  "foreboding landscape":   [ "swamp",    "forest",   "island" ],
  "perilous landscape":     [ "island",   "mountain", "plains" ],
  "seething landscape":     [ "island",   "swamp",    "mountain" ],
  "shattered landscape":    [ "mountain", "plains",   "swamp" ],
  "sheltering landscape":   [ "mountain", "forest",   "plains" ],
  "tranquil landscape":     [ "forest",   "plains",   "island" ],
  "twisted landscape":      [ "swamp",    "mountain", "forest" ]
});
